import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, FileText, Calendar, BookOpen, Headphones, 
  Video, Mail, Download, Eye, Clock, Check, X, AlertTriangle,
  ChevronRight, Search, Filter, MoreVertical, RefreshCw,
  GraduationCap, Award, TrendingUp, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { formatLocalDate } from '../lib/dateHelpers';

/**
 * COMPREHENSIVE MENTEE PROFILE PAGE
 * 6-tab view for admins/mentors to track mentee activity
 */

const MenteeProfile = () => {
  const { menteeId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('documents');
  const [profileData, setProfileData] = useState(null);
  
  // Modal states
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [attendanceStatus, setAttendanceStatus] = useState('completed');
  const [attendanceNotes, setAttendanceNotes] = useState('');
  const [lateMinutes, setLateMinutes] = useState(0);
  
  // Live session attendance modal
  const [showLiveSessionModal, setShowLiveSessionModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionAttendanceStatus, setSessionAttendanceStatus] = useState('attended');
  const [sessionEntryTime, setSessionEntryTime] = useState('');
  const [sessionNotes, setSessionNotes] = useState('');
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('access_token');
  
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    fetchProfileData();
  }, [menteeId]);

  const fetchProfileData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/mentee-profile/${menteeId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setProfileData(data);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to load mentee profile');
        navigate('/control-center');
      }
    } catch (e) {
      toast.error('Error loading mentee profile');
      navigate('/control-center');
    } finally {
      setLoading(false);
    }
  };

  // Handle document download with admin notification
  const handleDownloadDocument = async (doc) => {
    try {
      // Log the download first (notifies other admins)
      await fetch(`${API_URL}/api/mentee-profile/document/${doc.id}/download-log`, {
        method: 'POST',
        headers
      });
      
      // Perform the actual download
      const downloadUrl = doc.file_url.startsWith('http') 
        ? doc.file_url 
        : `${API_URL}${doc.file_url}`;
      
      const res = await fetch(downloadUrl, { headers });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.file_name || doc.title || 'download';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('File downloaded - Other admins have been notified');
      } else {
        toast.error('Failed to download file');
      }
    } catch (e) {
      toast.error('Error downloading file');
    }
  };

  // Update appointment attendance
  const handleUpdateAttendance = async () => {
    if (!selectedAppointment) return;
    
    try {
      const res = await fetch(`${API_URL}/api/mentee-profile/appointment/${selectedAppointment.id}/attendance`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          status: attendanceStatus,
          notes: attendanceNotes,
          late_minutes: attendanceStatus === 'late' ? lateMinutes : null
        })
      });
      
      if (res.ok) {
        toast.success('Attendance status updated');
        setShowAttendanceModal(false);
        setSelectedAppointment(null);
        setAttendanceNotes('');
        setLateMinutes(0);
        fetchProfileData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update attendance');
      }
    } catch (e) {
      toast.error('Error updating attendance');
    }
  };

  // Record live session attendance
  const handleRecordSessionAttendance = async () => {
    if (!selectedSession) return;
    
    try {
      const res = await fetch(`${API_URL}/api/mentee-profile/${menteeId}/live-session/attendance`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          session_id: selectedSession.session_id || selectedSession.id,
          status: sessionAttendanceStatus,
          entry_time: sessionEntryTime || null,
          notes: sessionNotes || null
        })
      });
      
      if (res.ok) {
        toast.success('Live session attendance recorded');
        setShowLiveSessionModal(false);
        setSelectedSession(null);
        setSessionNotes('');
        setSessionEntryTime('');
        fetchProfileData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to record attendance');
      }
    } catch (e) {
      toast.error('Error recording attendance');
    }
  };

  // Tab definitions
  const tabs = [
    { id: 'documents', label: 'Uploaded Documents', icon: FileText, count: profileData?.documents?.total || 0 },
    { id: 'appointments', label: 'Appointments', icon: Calendar, count: (profileData?.appointments?.total_upcoming || 0) + (profileData?.appointments?.total_past || 0) },
    { id: 'courses', label: 'Course Progress', icon: GraduationCap, count: profileData?.course_progress?.courses?.length || 0 },
    { id: 'ebooks', label: 'Ebook Tracking', icon: BookOpen, count: profileData?.ebooks?.total || 0 },
    { id: 'sessions', label: 'Live Sessions', icon: Video, count: profileData?.live_sessions?.total || 0 },
    { id: 'emails', label: 'Email Log', icon: Mail, count: profileData?.emails?.total || 0 },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading mentee profile...</p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">Mentee not found</p>
          <button 
            onClick={() => navigate('/control-center')}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg"
          >
            Back to Control Center
          </button>
        </div>
      </div>
    );
  }

  const { mentee, documents, appointments, course_progress, ebooks, live_sessions, emails } = profileData;

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Header */}
      <div className="bg-white border-b sticky top-16 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/control-center')}
                className="p-2 hover:bg-gray-100 rounded-lg"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-4">
                {mentee.profile_picture ? (
                  <img 
                    src={mentee.profile_picture} 
                    alt={mentee.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-purple-600" />
                  </div>
                )}
                
                <div>
                  <h1 className="text-xl font-bold text-gray-900" data-testid="mentee-name">
                    {mentee.name || mentee.email}
                  </h1>
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    <span>{mentee.email}</span>
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                      {mentee.mentorship_level || 'Disciple'}
                    </span>
                    <span className="text-orange-600 font-medium">{mentee.points || 0} pts</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={fetchProfileData}
                className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>
          
          {/* Tabs - Scrollable on mobile */}
          <div className="mt-4 -mb-px overflow-x-auto">
            <div className="flex gap-1 min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id 
                      ? 'border-purple-600 text-purple-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span className="font-medium">{tab.label}</span>
                  {tab.count > 0 && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      activeTab === tab.id ? 'bg-purple-100' : 'bg-gray-100'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        
        {/* ============== DOCUMENTS TAB ============== */}
        {activeTab === 'documents' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Uploaded Documents ({documents.total})
              </h2>
            </div>
            
            {documents.items.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No documents uploaded yet</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {documents.items.map((doc) => (
                  <div 
                    key={doc.id} 
                    className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    data-testid={`document-${doc.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                        doc.status === 'approved' ? 'bg-green-100 text-green-700' :
                        doc.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {doc.status?.charAt(0).toUpperCase() + doc.status?.slice(1) || 'Pending'}
                      </span>
                    </div>
                    
                    <h3 className="font-medium text-gray-900 mb-1 truncate" title={doc.title}>
                      {doc.title || doc.file_name}
                    </h3>
                    
                    <div className="text-sm text-gray-500 space-y-1 mb-3">
                      <p className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Unknown date'}
                      </p>
                      {doc.category && (
                        <p className="px-2 py-0.5 bg-gray-100 rounded-full text-xs inline-block">
                          {doc.category}
                        </p>
                      )}
                    </div>
                    
                    {doc.review_feedback && (
                      <div className="mb-3 p-2 bg-gray-50 rounded-lg text-sm">
                        <p className="text-gray-500 text-xs mb-1">Feedback:</p>
                        <p className="text-gray-700 italic">"{doc.review_feedback}"</p>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <button 
                        onClick={() => setSelectedDocument(doc)}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium"
                        data-testid={`view-doc-${doc.id}`}
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                      <button 
                        onClick={() => handleDownloadDocument(doc)}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm font-medium"
                        data-testid={`download-doc-${doc.id}`}
                        disabled={!doc.file_exists}
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ============== APPOINTMENTS TAB ============== */}
        {activeTab === 'appointments' && (
          <div className="space-y-6">
            {/* Upcoming Appointments */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Upcoming Appointments ({appointments.total_upcoming})
              </h2>
              
              {appointments.upcoming.length === 0 ? (
                <div className="bg-white rounded-xl border p-8 text-center">
                  <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No upcoming appointments</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {appointments.upcoming.map((apt) => (
                    <div 
                      key={apt.id} 
                      className="bg-white rounded-xl border p-4 flex items-center justify-between"
                      data-testid={`upcoming-apt-${apt.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                          <Calendar className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {apt.date ? formatLocalDate(apt.date, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : 'No date'}
                          </p>
                          <p className="text-sm text-gray-500">{apt.time || 'Time TBD'} • {apt.type || 'Mentorship'}</p>
                          {apt.topic && <p className="text-sm text-gray-600 mt-1">Topic: {apt.topic}</p>}
                        </div>
                      </div>
                      <span className={`px-3 py-1 text-sm rounded-full font-medium ${
                        apt.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {apt.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Past Appointments with Attendance Tracking */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Past Appointments ({appointments.total_past})
              </h2>
              
              {appointments.past.length === 0 ? (
                <div className="bg-white rounded-xl border p-8 text-center">
                  <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No past appointments</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl border overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attendance</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {appointments.past.map((apt) => (
                        <tr key={apt.id} className="hover:bg-gray-50" data-testid={`past-apt-${apt.id}`}>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">
                                {apt.date ? formatLocalDate(apt.date, { day: 'numeric', month: 'short' }) : 'N/A'}
                              </p>
                              <p className="text-xs text-gray-500">{apt.time}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell">
                            <span className="text-sm text-gray-600">{apt.type || 'Mentorship'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              apt.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                              apt.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {apt.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {apt.attendance_status ? (
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                apt.attendance_status === 'completed' || apt.attendance_status === 'on_time' ? 'bg-green-100 text-green-700' :
                                apt.attendance_status === 'late' ? 'bg-yellow-100 text-yellow-700' :
                                apt.attendance_status === 'no_show' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {apt.attendance_status === 'no_show' ? 'Did Not Show' :
                                 apt.attendance_status === 'late' ? `Late (${apt.late_minutes || '?'}min)` :
                                 apt.attendance_status.charAt(0).toUpperCase() + apt.attendance_status.slice(1)}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">Not recorded</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <button 
                              onClick={() => {
                                setSelectedAppointment(apt);
                                setAttendanceStatus(apt.attendance_status || 'completed');
                                setAttendanceNotes(apt.attendance_notes || '');
                                setLateMinutes(apt.late_minutes || 0);
                                setShowAttendanceModal(true);
                              }}
                              className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                              data-testid={`update-attendance-${apt.id}`}
                            >
                              {apt.attendance_status ? 'Update' : 'Mark Attendance'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============== COURSE PROGRESS TAB ============== */}
        {activeTab === 'courses' && (
          <div className="space-y-6">
            {/* Course Progress */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Enrolled Courses ({course_progress.courses.length})
              </h2>
              
              {course_progress.courses.length === 0 ? (
                <div className="bg-white rounded-xl border p-12 text-center">
                  <GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Not enrolled in any courses yet</p>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  {course_progress.courses.map((course, idx) => (
                    <div 
                      key={idx} 
                      className="bg-white rounded-xl border p-5"
                      data-testid={`course-progress-${course.course_id}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-gray-900">{course.course_title}</h3>
                          <p className="text-sm text-gray-500">
                            {course.completed_lessons} of {course.total_lessons} lessons completed
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-purple-600">{course.progress_percentage}%</p>
                        </div>
                      </div>
                      
                      {/* Progress bar */}
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                        <div 
                          className="bg-purple-600 h-2 rounded-full transition-all"
                          style={{ width: `${course.progress_percentage}%` }}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>Started: {course.started_at ? new Date(course.started_at).toLocaleDateString('en-GB') : 'N/A'}</span>
                        {course.completed_at && (
                          <span className="text-green-600 font-medium flex items-center gap-1">
                            <Check className="w-4 h-4" /> Completed
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Exam Submissions */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Exam Submissions ({course_progress.exams.length})
              </h2>
              
              {course_progress.exams.length === 0 ? (
                <div className="bg-white rounded-xl border p-8 text-center">
                  <Award className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No exams taken yet</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl border overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exam</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Result</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {course_progress.exams.map((exam) => (
                        <tr key={exam.id} className="hover:bg-gray-50" data-testid={`exam-${exam.id}`}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900 text-sm">{exam.exam_title}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-semibold text-gray-900">
                              {exam.score}/{exam.total_questions}
                              <span className="text-gray-500 font-normal ml-1">({exam.percentage}%)</span>
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                              exam.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {exam.passed ? 'Passed' : 'Failed'}
                            </span>
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell">
                            <span className="text-sm text-gray-500">
                              {exam.submitted_at ? new Date(exam.submitted_at).toLocaleDateString('en-GB') : 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============== EBOOK TRACKING TAB ============== */}
        {activeTab === 'ebooks' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Ebook Reading Progress ({ebooks.total})
            </h2>
            
            {ebooks.items.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center">
                <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No ebooks started yet</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {ebooks.items.map((ebook, idx) => (
                  <div 
                    key={idx} 
                    className="bg-white rounded-xl border p-5"
                    data-testid={`ebook-${ebook.ebook_id}`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-12 h-16 bg-gradient-to-br from-purple-500 to-orange-500 rounded flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 line-clamp-2">{ebook.ebook_title}</h3>
                        <p className="text-sm text-gray-500">
                          Page {ebook.current_page} of {ebook.total_pages}
                        </p>
                      </div>
                    </div>
                    
                    {/* Progress bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                      <div 
                        className="bg-gradient-to-r from-purple-500 to-orange-500 h-2 rounded-full transition-all"
                        style={{ width: `${ebook.progress_percentage}%` }}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">
                        {ebook.progress_percentage}% complete
                      </span>
                      {ebook.completed && (
                        <span className="text-green-600 font-medium flex items-center gap-1">
                          <Check className="w-4 h-4" /> Finished
                        </span>
                      )}
                    </div>
                    
                    {ebook.last_read && (
                      <p className="text-xs text-gray-400 mt-2">
                        Last read: {new Date(ebook.last_read).toLocaleDateString('en-GB')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ============== LIVE SESSIONS TAB ============== */}
        {activeTab === 'sessions' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Live Session Attendance ({live_sessions.total})
              </h2>
            </div>
            
            {live_sessions.items.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center">
                <Video className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No live session attendance recorded</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attendance</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Notes</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {live_sessions.items.map((session) => (
                      <tr key={session.id} className="hover:bg-gray-50" data-testid={`session-${session.id}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900 text-sm">{session.session_title}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-600">
                            {session.session_date ? new Date(session.session_date).toLocaleDateString('en-GB') : 'N/A'}
                          </p>
                          <p className="text-xs text-gray-400">{session.session_time}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                            session.attendance_status === 'attended' ? 'bg-green-100 text-green-700' :
                            session.attendance_status === 'late' ? 'bg-yellow-100 text-yellow-700' :
                            session.attendance_status === 'no_show' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {session.attendance_status === 'no_show' ? 'Did Not Show' :
                             session.attendance_status?.charAt(0).toUpperCase() + session.attendance_status?.slice(1)}
                          </span>
                          {session.entry_time && (
                            <p className="text-xs text-gray-500 mt-1">Joined: {session.entry_time}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <p className="text-sm text-gray-500 truncate max-w-xs" title={session.notes}>
                            {session.notes || '-'}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <button 
                            onClick={() => {
                              setSelectedSession(session);
                              setSessionAttendanceStatus(session.attendance_status || 'attended');
                              setSessionEntryTime(session.entry_time || '');
                              setSessionNotes(session.notes || '');
                              setShowLiveSessionModal(true);
                            }}
                            className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                            data-testid={`update-session-${session.id}`}
                          >
                            Update
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ============== EMAIL LOG TAB ============== */}
        {activeTab === 'emails' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Email Communication Log ({emails.total})
            </h2>
            
            {emails.items.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center">
                <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No email communications logged</p>
              </div>
            ) : (
              <div className="space-y-3">
                {emails.items.map((email) => (
                  <div 
                    key={email.id} 
                    className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setSelectedEmail(email)}
                    data-testid={`email-${email.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          email.email_type === 'manual' ? 'bg-purple-100' : 'bg-blue-100'
                        }`}>
                          <Mail className={`w-5 h-5 ${
                            email.email_type === 'manual' ? 'text-purple-600' : 'text-blue-600'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-gray-900 truncate">{email.subject}</h3>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                              email.email_type === 'manual' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {email.email_type === 'manual' ? 'Manual' : 'System'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                            {email.content_preview || 'No preview available'}
                          </p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                            <span>{email.sent_at ? new Date(email.sent_at).toLocaleString('en-GB') : 'Unknown'}</span>
                            {email.sender && <span>From: {email.sender}</span>}
                          </div>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full shrink-0 ${
                        email.status === 'sent' ? 'bg-green-100 text-green-700' :
                        email.status === 'failed' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {email.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ============== MODALS ============== */}
      
      {/* Document Viewer Modal */}
      {selectedDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between bg-gray-50">
              <h2 className="font-semibold text-gray-900">Document Details</h2>
              <button 
                onClick={() => setSelectedDocument(null)}
                className="p-2 hover:bg-gray-200 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{selectedDocument.title || selectedDocument.file_name}</h3>
                    <p className="text-gray-500">{selectedDocument.file_name}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <p className="text-sm text-gray-500">Category</p>
                    <p className="font-medium">{selectedDocument.category || 'Uncategorized'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <p className={`font-medium ${
                      selectedDocument.status === 'approved' ? 'text-green-600' :
                      selectedDocument.status === 'rejected' ? 'text-red-600' :
                      'text-yellow-600'
                    }`}>
                      {selectedDocument.status?.charAt(0).toUpperCase() + selectedDocument.status?.slice(1) || 'Pending'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Uploaded</p>
                    <p className="font-medium">
                      {selectedDocument.uploaded_at ? new Date(selectedDocument.uploaded_at).toLocaleString('en-GB') : 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">File Type</p>
                    <p className="font-medium">{selectedDocument.file_type || 'Unknown'}</p>
                  </div>
                </div>
                
                {selectedDocument.review_feedback && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-500 mb-2">Mentor Feedback</p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700 italic">"{selectedDocument.review_feedback}"</p>
                      {selectedDocument.reviewed_at && (
                        <p className="text-xs text-gray-400 mt-2">
                          Reviewed: {new Date(selectedDocument.reviewed_at).toLocaleString('en-GB')}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
              <button 
                onClick={() => setSelectedDocument(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Close
              </button>
              <button 
                onClick={() => {
                  handleDownloadDocument(selectedDocument);
                  setSelectedDocument(null);
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download File
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Viewer Modal */}
      {selectedEmail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between bg-gray-50">
              <h2 className="font-semibold text-gray-900">Email Details</h2>
              <button 
                onClick={() => setSelectedEmail(null)}
                className="p-2 hover:bg-gray-200 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{selectedEmail.subject}</h3>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    <span>From: {selectedEmail.sender || 'System'}</span>
                    <span>{selectedEmail.sent_at ? new Date(selectedEmail.sent_at).toLocaleString('en-GB') : 'Unknown'}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      selectedEmail.status === 'sent' ? 'bg-green-100 text-green-700' :
                      selectedEmail.status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {selectedEmail.status}
                    </span>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-500 mb-2">Email Content</p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    {selectedEmail.full_content ? (
                      <div 
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: selectedEmail.full_content }}
                      />
                    ) : (
                      <p className="text-gray-600">{selectedEmail.content_preview || 'No content available'}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <button 
                onClick={() => setSelectedEmail(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Attendance Modal */}
      {showAttendanceModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-4 border-b">
              <h2 className="font-semibold text-gray-900">Update Attendance</h2>
              <p className="text-sm text-gray-500 mt-1">
                Appointment: {selectedAppointment.date ? formatLocalDate(selectedAppointment.date) : 'N/A'} at {selectedAppointment.time}
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Attendance Status</label>
                <select
                  value={attendanceStatus}
                  onChange={(e) => setAttendanceStatus(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  data-testid="attendance-status-select"
                >
                  <option value="completed">Completed</option>
                  <option value="on_time">On Time</option>
                  <option value="late">Arrived Late</option>
                  <option value="no_show">Did Not Show</option>
                </select>
              </div>
              
              {attendanceStatus === 'late' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Minutes Late</label>
                  <input
                    type="number"
                    min="1"
                    value={lateMinutes}
                    onChange={(e) => setLateMinutes(parseInt(e.target.value) || 0)}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="e.g., 15"
                    data-testid="late-minutes-input"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes (optional)</label>
                <textarea
                  value={attendanceNotes}
                  onChange={(e) => setAttendanceNotes(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  rows="3"
                  placeholder="Add any notes about this attendance..."
                  data-testid="attendance-notes-input"
                />
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
              <button 
                onClick={() => {
                  setShowAttendanceModal(false);
                  setSelectedAppointment(null);
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button 
                onClick={handleUpdateAttendance}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                data-testid="save-attendance-btn"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live Session Attendance Modal */}
      {showLiveSessionModal && selectedSession && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-4 border-b">
              <h2 className="font-semibold text-gray-900">Record Session Attendance</h2>
              <p className="text-sm text-gray-500 mt-1">{selectedSession.session_title}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Attendance Status</label>
                <select
                  value={sessionAttendanceStatus}
                  onChange={(e) => setSessionAttendanceStatus(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  data-testid="session-status-select"
                >
                  <option value="attended">Attended</option>
                  <option value="late">Arrived Late</option>
                  <option value="no_show">Did Not Show</option>
                </select>
              </div>
              
              {sessionAttendanceStatus === 'late' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Entry Time</label>
                  <input
                    type="time"
                    value={sessionEntryTime}
                    onChange={(e) => setSessionEntryTime(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    data-testid="session-entry-time"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes (optional)</label>
                <textarea
                  value={sessionNotes}
                  onChange={(e) => setSessionNotes(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  rows="3"
                  placeholder="Add any notes about attendance..."
                  data-testid="session-notes-input"
                />
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
              <button 
                onClick={() => {
                  setShowLiveSessionModal(false);
                  setSelectedSession(null);
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button 
                onClick={handleRecordSessionAttendance}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                data-testid="save-session-attendance-btn"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenteeProfile;
